<template>
  <div class="admin-shell">

    <!-- Sidebar zone — hover here expands the sidebar -->
    <div
      class="admin-sidebar-zone"
      :class="{
        'admin-sidebar-zone--expanded': isSidebarExpanded,
        'admin-sidebar-zone--mobile-open': isMobileOpen,
      }"
      @mouseenter="onSidebarMouseEnter"
      @mouseleave="onSidebarMouseLeave"
    >
      <AdminSidebar :collapsed="!isSidebarExpanded" @toggle="toggleSidebar" />
    </div>

    <!-- Main content shifts right when sidebar is pinned expanded -->
    <div
      class="admin-main"
      :class="{ 'admin-main--expanded': isSidebarExpanded && !isMobile }"
    >
      <AdminTopbar :toggleSidebar="toggleSidebar" />

      <main class="admin-content">
        <router-view />
      </main>

      <footer class="admin-footer">
        BORAsuite Admin Control Center
      </footer>
    </div>

    <!-- Mobile overlay -->
    <div
      v-if="isMobile && isMobileOpen"
      class="admin-sidebar-overlay"
      @click="isMobileOpen = false"
    ></div>

  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import AdminSidebar from '../components/admin/AdminSidebar.vue'
import AdminTopbar from '../components/admin/AdminTopbar.vue'

const STORAGE_KEY = 'borasuite_admin_sidebar_pinned'
const DESKTOP_BREAKPOINT = 1024

// ── State ─────────────────────────────────────────────────────────────────────
// isManuallyExpanded = user clicked the topbar burger to PIN the sidebar open
// isHoverExpanded    = mouse is currently over the sidebar zone
const isManuallyExpanded = ref<boolean>(readPersistedPinState())
const isHoverExpanded = ref(false)
const isMobile = ref(false)
const isMobileOpen = ref(false)

// ── Computed ──────────────────────────────────────────────────────────────────
const isSidebarExpanded = computed(() => {
  if (isMobile.value) return isMobileOpen.value
  return isManuallyExpanded.value || isHoverExpanded.value
})

// ── Viewport tracking ─────────────────────────────────────────────────────────
function evaluateViewport() {
  isMobile.value = window.innerWidth <= DESKTOP_BREAKPOINT
  if (!isMobile.value) isMobileOpen.value = false
}

// ── Hover handlers ────────────────────────────────────────────────────────────
function onSidebarMouseEnter() {
  // Don't layer hover expand over a pinned sidebar — already expanded
  if (isMobile.value || isManuallyExpanded.value) return
  isHoverExpanded.value = true
}

function onSidebarMouseLeave() {
  if (isMobile.value || isManuallyExpanded.value) return
  isHoverExpanded.value = false
}

// ── Toggle (topbar burger / sidebar button) ───────────────────────────────────
function toggleSidebar() {
  if (isMobile.value) {
    isMobileOpen.value = !isMobileOpen.value
    return
  }
  isManuallyExpanded.value = !isManuallyExpanded.value
  // If un-pinning, also clear any hover state
  if (!isManuallyExpanded.value) isHoverExpanded.value = false
  persistPinState(isManuallyExpanded.value)
}

// ── localStorage ──────────────────────────────────────────────────────────────
function readPersistedPinState(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) === 'true' } catch { return false }
}

function persistPinState(value: boolean) {
  try { localStorage.setItem(STORAGE_KEY, String(value)) } catch { /* ignore */ }
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────
onMounted(() => {
  evaluateViewport()
  window.addEventListener('resize', evaluateViewport)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', evaluateViewport)
})
</script>

<style scoped>
/* ── Sidebar zone ──────────────────────────────────────────────────────────── */
.admin-sidebar-zone {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  /* Collapsed width — matches .admin-sidebar width */
  width: 72px;
  z-index: 100;
  transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

.admin-sidebar-zone--expanded {
  width: 270px;
}

/* ── Main area ─────────────────────────────────────────────────────────────── */
.admin-main {
  margin-left: 72px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  /* Smooth shift when sidebar is PINNED expanded (not just hovered) */
  transition: margin-left 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

.admin-main--expanded {
  margin-left: 270px;
}

/* ── Mobile overlay ────────────────────────────────────────────────────────── */
.admin-sidebar-overlay {
  position: fixed;
  inset: 0;
  background: rgba(11, 19, 34, 0.45);
  z-index: 99;
}

/* ── Mobile: sidebar slides in ─────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .admin-sidebar-zone {
    width: 270px;
    transform: translateX(-100%);
    transition: transform 0.22s ease;
  }

  .admin-sidebar-zone--mobile-open {
    transform: translateX(0);
  }

  .admin-main,
  .admin-main--expanded {
    margin-left: 0;
  }
}
</style>
